import { useEffect, useMemo, useRef } from "react";
import { useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import {
  MarkerClusterer,
  SuperClusterAlgorithm,
  type Cluster,
} from "@googlemaps/markerclusterer";
import {
  CITY_ZOOM,
  COUNTRY_ZOOM,
  cityCoreVenues,
  clampedZoom,
  clusterCanSplit,
  createClusterRenderer,
} from "./brand-map";
import { styleForItem } from "./category-style";
import { zoomBand, type ZoomBand } from "./zoom-band";
import { hasImage } from "./thumb-url";
import { photoPinHtml, fallbackDiscHtml, categoryGlyph } from "./marker-content";
import type { DiscoverItem } from "../../types";

// Imperative map controllers + clustered pin layer shared by the desktop
// DiscoverStage map and the mobile map backdrops.

// Controllers only fire when the prop value changes (city pick, zoom button).
// Without the effect-deps gate they ran on every render and snapped the map
// back to the configured center after every user interaction (cluster zoom,
// pan), which made cluster clicks appear to recenter on Morocco.
export function ZoomController({ zoom }: { readonly zoom: number }) {
  const map = useMap();
  useEffect(() => {
    if (!map) return;
    map.setZoom(zoom);
  }, [map, zoom]);
  return null;
}

export function CenterController({
  lat,
  lng,
}: {
  readonly lat: number;
  readonly lng: number;
}) {
  const map = useMap();
  useEffect(() => {
    if (!map) return;
    map.panTo({ lat, lng });
  }, [map, lat, lng]);
  return null;
}

// Frame the map on the actual visible pins instead of a stored city centroid.
// The city rows' latitude/longitude can drift or be wrong; the pins carry the
// real coordinates, so fitting to them keeps the map correct regardless. Only
// re-fits when `fitKey` changes (city pick, tab/visibility switch, item count)
// — never on pan/zoom — so user interaction isn't snapped back. Latest items /
// fallback are read through refs so a re-render alone can't retrigger a fit.
export function FitBoundsController({
  fitKey,
  items,
  fallback,
  hasCity,
  maxZoom = CITY_ZOOM,
}: {
  readonly fitKey: string;
  readonly items: readonly DiscoverItem[];
  readonly fallback: { readonly lat: number; readonly lng: number };
  readonly hasCity: boolean;
  readonly maxZoom?: number;
}) {
  const map = useMap();
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const fallbackRef = useRef(fallback);
  fallbackRef.current = fallback;
  const hasCityRef = useRef(hasCity);
  hasCityRef.current = hasCity;
  useEffect(() => {
    if (!map) return undefined;
    const pts = itemsRef.current.filter(
      (i) => typeof i.latitude === "number" && typeof i.longitude === "number",
    );
    if (pts.length === 0) {
      map.panTo(fallbackRef.current);
      map.setZoom(hasCityRef.current ? CITY_ZOOM : COUNTRY_ZOOM);
      return undefined;
    }
    if (pts.length === 1) {
      const p = pts[0]!;
      map.panTo({ lat: p.latitude as number, lng: p.longitude as number });
      map.setZoom(CITY_ZOOM);
      return undefined;
    }
    // With a city picked, frame that city and let its day trips fall off the
    // edge: an Agafay camp sold under Marrakech would otherwise hold the camera
    // out over the desert, and picking a city would never look like it did
    // anything. Country view frames everything, outliers included.
    const framed = hasCityRef.current ? cityCoreVenues(pts) : pts;
    const bounds = new google.maps.LatLngBounds();
    for (const p of framed) {
      bounds.extend({ lat: p.latitude as number, lng: p.longitude as number });
    }
    map.fitBounds(bounds, 64);
    // fitBounds picks the tightest zoom that frames the bounds; clamp it so a
    // dense one-city cluster can't blow past the browse threshold
    // (VIEWPORT_SYNC_ZOOM) and flip the list into "places in view" mode.
    const listener = map.addListener("idle", () => {
      const z = map.getZoom();
      if (typeof z === "number" && z > maxZoom) map.setZoom(maxZoom);
      listener.remove();
    });
    return () => listener.remove();
  }, [map, fitKey, maxZoom]);
  return null;
}

// Zoom +/- buttons live outside the map's React context, so they signal intent
// via a monotonic `nudge` counter; this applies the delta to the LIVE camera
// zoom (not an absolute derived from the city), so it composes with fit-to-pins
// framing instead of fighting it.
export function ZoomNudgeController({ nudge }: { readonly nudge: number }) {
  const map = useMap();
  const prevRef = useRef(nudge);
  useEffect(() => {
    if (!map) return;
    const delta = nudge - prevRef.current;
    prevRef.current = nudge;
    if (delta === 0) return;
    map.setZoom(clampedZoom(map.getZoom() ?? COUNTRY_ZOOM, delta));
  }, [map, nudge]);
  return null;
}

// Closes the open InfoWindow when the user clicks anywhere on the map
// background (Google Maps fires `click` on the map for empty-area taps but
// suppresses it for marker hits). Without this the popup persists until the
// user taps the X.
export function MapClickCloser({ onClose }: { readonly onClose: () => void }) {
  const map = useMap();
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);
  useEffect(() => {
    if (!map) return undefined;
    const listener = map.addListener("click", () => onCloseRef.current());
    return () => listener.remove();
  }, [map]);
  return null;
}

interface ClusteredPinsProps {
  readonly items: readonly DiscoverItem[];
  readonly infoItemId: string | null;
  readonly onSelect: (id: string) => void;
  /** Mobile shrinks pins to dots one zoom level earlier. */
  readonly isMobile?: boolean;
  /**
   * Called with a bubble's members when no amount of zoom would separate them,
   * so the surface can list the venues instead. Without it a bubble that can
   * never come apart is a dead end, which is what it used to be.
   */
  readonly onClusterMembers?: (ids: readonly string[]) => void;
}

interface MarkerEntry {
  readonly item: DiscoverItem;
  readonly el: HTMLDivElement;
  readonly marker: google.maps.marker.AdvancedMarkerElement;
}

// Build a marker's inner DOM ONCE — a pin layer (photo or fallback disc) plus a
// dot layer — then let `data-band` toggle which is shown via CSS. Because the
// photo <img> node is never destroyed on a zoom-band change, the thumbnail is
// not re-fetched/re-decoded when the user crosses the street zoom repeatedly.
// The container is a role="button" with a label + key handler, so the map is
// fully keyboard- and screen-reader-operable, not mouse/touch only.
function buildMarkerEl(item: DiscoverItem, band: ZoomBand): HTMLDivElement {
  const style = styleForItem(item);
  const el = document.createElement("div");
  el.className = "mj-marker";
  el.style.setProperty("--cat", style.color);
  el.dataset.band = band;
  el.tabIndex = 0;
  el.setAttribute("role", "button");
  el.setAttribute("aria-label", item.title);
  const pin = hasImage(item) ? photoPinHtml(item, style) : fallbackDiscHtml(style);
  el.innerHTML =
    `<div class="mj-pin">${pin}</div>` +
    `<div class="mj-dot" aria-hidden="true">${categoryGlyph(style)}</div>`;
  return el;
}

export function ClusteredPins({
  items,
  infoItemId,
  onSelect,
  isMobile = false,
  onClusterMembers,
}: ClusteredPinsProps) {
  const map = useMap();
  const markerLib = useMapsLibrary("marker");
  const onSelectRef = useRef(onSelect);
  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);
  const onClusterMembersRef = useRef(onClusterMembers);
  useEffect(() => {
    onClusterMembersRef.current = onClusterMembers;
  }, [onClusterMembers]);
  const infoItemIdRef = useRef(infoItemId);
  const entriesRef = useRef<Map<string, MarkerEntry>>(new Map());
  // Which venue a marker stands for, so a tapped bubble can name its members.
  const itemByMarkerRef = useRef<Map<object, DiscoverItem>>(new Map());
  const bandRef = useRef<ZoomBand>("city");

  const clusterer = useMemo(() => {
    if (!map || !markerLib) return null;
    const algorithm = new SuperClusterAlgorithm({
      radius: 140,
      maxZoom: 14,
      minPoints: 2,
    });
    // Tapping a bubble. Zooming in is the answer while a closer look still
    // takes the bubble apart; for venues stacked on one coordinate it never
    // will, so those are handed to the surface to be listed instead. Both the
    // mouse and the keyboard come through here.
    const activateCluster = (cluster: Cluster, m: google.maps.Map) => {
      const centroid = cluster.position;
      if (!centroid) return;
      const lat = typeof centroid.lat === "function" ? centroid.lat() : centroid.lat;
      const lng = typeof centroid.lng === "function" ? centroid.lng() : centroid.lng;
      if (typeof lat !== "number" || typeof lng !== "number") return;
      const members = cluster.markers
        .map((marker) => itemByMarkerRef.current.get(marker as object))
        .filter((item): item is DiscoverItem => item != null);
      const points = members.map((item) => ({
        lat: item.latitude as number,
        lng: item.longitude as number,
      }));
      if (
        onClusterMembersRef.current &&
        members.length > 1 &&
        !clusterCanSplit(points)
      ) {
        m.panTo({ lat, lng });
        onClusterMembersRef.current(members.map((item) => item.id));
        return;
      }
      // fitBounds is a no-op at low zoom on country-wide clusters, so the
      // camera advances by a fixed delta instead.
      m.setCenter({ lat, lng });
      m.setZoom(clampedZoom(m.getZoom() ?? COUNTRY_ZOOM, 3));
    };

    return new MarkerClusterer({
      map,
      renderer: createClusterRenderer(activateCluster),
      algorithm,
      onClusterClick: (_event, cluster, m) => activateCluster(cluster, m),
    });
  }, [map, markerLib]);

  // Build advanced markers for the current items and hand them to the clusterer.
  useEffect(() => {
    if (!map || !clusterer || !markerLib) return undefined;
    const { AdvancedMarkerElement } = markerLib as google.maps.MarkerLibrary;
    const band = zoomBand(map.getZoom() ?? COUNTRY_ZOOM, isMobile);
    bandRef.current = band;
    const entries = new Map<string, MarkerEntry>();
    for (const item of items) {
      if (typeof item.latitude !== "number" || typeof item.longitude !== "number") {
        continue;
      }
      const el = buildMarkerEl(item, band);
      el.dataset.selected = infoItemIdRef.current === item.id ? "true" : "false";
      const marker = new AdvancedMarkerElement({
        position: { lat: item.latitude, lng: item.longitude },
        content: el,
        title: item.title,
      });
      const entry: MarkerEntry = { item, el, marker };
      const select = (e: Event) => {
        e.stopPropagation();
        onSelectRef.current(item.id);
      };
      el.addEventListener("click", select);
      el.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          select(e);
        }
      });
      entries.set(item.id, entry);
      itemByMarkerRef.current.set(marker as object, item);
    }
    entriesRef.current = entries;
    clusterer.addMarkers([...entries.values()].map((en) => en.marker));
    return () => {
      clusterer.removeMarkers([...entries.values()].map((en) => en.marker));
      for (const en of entries.values()) {
        itemByMarkerRef.current.delete(en.marker as object);
        en.marker.map = null;
      }
      entriesRef.current = new Map();
    };
  }, [map, clusterer, markerLib, items, isMobile]);

  // Morph markers when the zoom band changes (photo pins ⇄ dots).
  useEffect(() => {
    if (!map) return undefined;
    const listener = map.addListener("zoom_changed", () => {
      const band = zoomBand(map.getZoom() ?? COUNTRY_ZOOM, isMobile);
      if (band === bandRef.current) return;
      bandRef.current = band;
      for (const entry of entriesRef.current.values()) {
        entry.el.dataset.band = band;
      }
    });
    return () => listener.remove();
  }, [map, isMobile]);

  // Repaint only the markers whose selection state changed.
  useEffect(() => {
    const prev = infoItemIdRef.current;
    infoItemIdRef.current = infoItemId;
    if (prev === infoItemId) return;
    if (prev) {
      const e = entriesRef.current.get(prev);
      if (e) e.el.dataset.selected = "false";
    }
    if (infoItemId) {
      const e = entriesRef.current.get(infoItemId);
      if (e) e.el.dataset.selected = "true";
    }
  }, [infoItemId]);

  return null;
}
